import { useCallback, useEffect, useId, useMemo, useState, type RefObject } from "react";
import { useQuery } from "convex/react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { queryRef } from "../../lib/convex/refs";
import { useDeviceId } from "../../lib/device/DeviceProvider";
import { useMarketingDemoOverlay } from "../../lib/marketing/MarketingDemoProvider";
import {
  addMonths,
  canNavigateCalendarMonthNext,
  canNavigateCalendarMonthPrev,
  isDateInMonth,
  isDateInWeek,
  isDateKeyAfter,
  isSameMonth,
  parseDateKey,
  resolveDatePickForMode,
  startOfMonth,
} from "../../lib/dateNav";
import { formatEnglishMonthLabel, todayKey } from "../../lib/format";
import type { HeatmapResult } from "../../lib/habits/heatmap";
import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";
import { getScreen0IntlLocale } from "@/features/language/language-registry";
import { CalendarVerticalScroller } from "../calendar/CalendarVerticalScroller";
import {
  buildInitialWeekStarts,
  buildWeekDays,
  ensureWeekStartsCoverMonth,
  heatmapRangeForWeekStarts,
  prependWeekCount,
} from "./calendarScrollLogic";
import type { PeriodMode } from "./todayTopBarTitle";

const POPOVER_SPRING = { type: "spring" as const, stiffness: 520, damping: 36, mass: 0.7 };
const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"] as const;
const heatmapRef = queryRef<
  { anonymousDeviceId: string; startDate: string; endDate: string },
  HeatmapResult
>("usageHeatmap:getHeatmap");

type DatePickerPopoverProps = {
  id?: string;
  open: boolean;
  mode: PeriodMode;
  selectedDate: string;
  referenceDate?: string;
  onSelectDate: (date: string) => void;
  onClose: () => void;
};

export function DatePickerPopover({
  id: idProp,
  open,
  mode,
  selectedDate,
  referenceDate = todayKey(),
  onSelectDate,
  onClose,
}: DatePickerPopoverProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const prefersReducedMotion = useReducedMotion();
  const anonymousDeviceId = useDeviceId();
  const generatedId = useId();
  const dialogId = idProp ?? generatedId;
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(selectedDate));
  const [weekStarts, setWeekStarts] = useState<string[]>(() =>
    buildInitialWeekStarts(selectedDate, referenceDate),
  );
  const [scrollRequest, setScrollRequest] = useState<{ month: string; nonce: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const month = startOfMonth(selectedDate);
    setVisibleMonth(month);
    setWeekStarts(buildInitialWeekStarts(selectedDate, referenceDate));
    setScrollRequest({ month, nonce: Date.now() });
  }, [open, referenceDate, selectedDate]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const heatmapRange = useMemo(
    () => heatmapRangeForWeekStarts(weekStarts, referenceDate),
    [referenceDate, weekStarts],
  );

  const heatmapDataReal = useQuery(
    heatmapRef,
    open ? { anonymousDeviceId, ...heatmapRange } : "skip",
  );
  const heatmapData = useMarketingDemoOverlay(
    "usageHeatmap:getHeatmap",
    open ? { anonymousDeviceId, ...heatmapRange } : "skip",
    heatmapDataReal,
  );

  const datesWithSnapshot = useMemo(
    () => new Set((heatmapData?.days ?? []).map((day) => day.date)),
    [heatmapData],
  );

  const canGoMonthPrev = canNavigateCalendarMonthPrev(visibleMonth);
  const canGoMonthNext = canNavigateCalendarMonthNext(visibleMonth, referenceDate);

  const goMonthPrev = useCallback(() => {
    if (!canGoMonthPrev) return;
    const month = startOfMonth(addMonths(visibleMonth, -1));
    setWeekStarts((current) => ensureWeekStartsCoverMonth(current, month, referenceDate));
    setVisibleMonth(month);
    setScrollRequest({ month, nonce: Date.now() });
  }, [canGoMonthPrev, referenceDate, visibleMonth]);

  const goMonthNext = useCallback(() => {
    if (!canGoMonthNext) return;
    const month = startOfMonth(addMonths(visibleMonth, 1));
    setWeekStarts((current) => ensureWeekStartsCoverMonth(current, month, referenceDate));
    setVisibleMonth(month);
    setScrollRequest({ month, nonce: Date.now() });
  }, [canGoMonthNext, referenceDate, visibleMonth]);

  const handleVisibleMonthChange = useCallback((month: string) => {
    setVisibleMonth((current) => (current === month ? current : month));
  }, []);

  const handlePrependWeeks = useCallback((count: number) => {
    setWeekStarts((current) => {
      if (current.length === 0) return current;
      return [...prependWeekCount(current[0]!, count), ...current];
    });
  }, []);

  const handleDayPick = useCallback(
    (day: string) => {
      if (isDateKeyAfter(day, referenceDate)) return;
      onSelectDate(resolveDatePickForMode(day, mode));
      onClose();
    },
    [mode, onClose, onSelectDate, referenceDate],
  );

  const monthLabel = formatEnglishMonthLabel(visibleMonth, { includeYear: true, languageCode });

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          id={dialogId}
          role="dialog"
          aria-label={t.calendar.chooseDate}
          aria-modal="true"
          className="liquid-glass-pill fixed left-1/2 z-50 w-[min(100vw-36px,20.5rem)] max-w-[calc(100vw-36px)] overflow-hidden rounded-2xl p-3 shadow-[0_16px_40px_oklch(0%_0_0/0.45)]"
          style={{
            top: "calc(max(18px, env(safe-area-inset-top)) + 2.5rem + 16px)",
            transformOrigin: "top center",
          }}
          initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8, scale: 0.96, x: "-50%" }}
          animate={{ opacity: 1, y: 0, scale: 1, x: "-50%" }}
          exit={prefersReducedMotion ? { opacity: 0, x: "-50%" } : { opacity: 0, y: -8, scale: 0.96, x: "-50%" }}
          transition={prefersReducedMotion ? { duration: 0 } : POPOVER_SPRING}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <button
              type="button"
              className="icon-button shrink-0"
              onClick={goMonthPrev}
              disabled={!canGoMonthPrev}
              aria-label={t.calendar.previousMonth}
            >
              <ChevronLeft size={18} />
            </button>
            <p className="min-w-0 truncate text-sm font-semibold text-[oklch(90%_0.02_150)]">{monthLabel}</p>
            <button
              type="button"
              className="icon-button shrink-0"
              onClick={goMonthNext}
              disabled={!canGoMonthNext}
              aria-label={t.calendar.nextMonth}
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="mb-1 grid grid-cols-7 gap-0.5">
            {WEEKDAY_LABELS.map((label, index) => (
              <span
                key={`${label}-${index}`}
                className="grid h-7 place-items-center text-[10px] font-semibold uppercase tracking-wide text-[oklch(58%_0.025_155)]"
                aria-hidden
              >
                {label}
              </span>
            ))}
          </div>

          <CalendarVerticalScroller
            weekStarts={weekStarts}
            visibleMonth={visibleMonth}
            scrollRequest={scrollRequest}
            selectedDate={selectedDate}
            onVisibleMonthChange={handleVisibleMonthChange}
            onPrependWeeks={handlePrependWeeks}
            renderWeekRow={({ weekStart, rowMeasureRef, visibleMonth: rowVisibleMonth }) => (
              <WeekRow
                weekStart={weekStart}
                rowMeasureRef={rowMeasureRef}
                visibleMonth={rowVisibleMonth}
                mode={mode}
                selectedDate={selectedDate}
                referenceDate={referenceDate}
                datesWithSnapshot={datesWithSnapshot}
                onPickDay={handleDayPick}
                t={t}
                languageCode={languageCode}
              />
            )}
          />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

type WeekRowProps = {
  weekStart: string;
  rowMeasureRef?: RefObject<HTMLDivElement | null>;
  visibleMonth: string;
  mode: PeriodMode;
  selectedDate: string;
  referenceDate: string;
  datesWithSnapshot: Set<string>;
  onPickDay: (day: string) => void;
  t: ReturnType<typeof getTranslations>;
  languageCode: ReturnType<typeof useScreen0Language>["languageCode"];
};

function WeekRow({
  weekStart,
  rowMeasureRef,
  visibleMonth,
  mode,
  selectedDate,
  referenceDate,
  datesWithSnapshot,
  onPickDay,
  t,
  languageCode,
}: WeekRowProps) {
  const days = buildWeekDays(weekStart);

  return (
    <div ref={rowMeasureRef} className="grid grid-cols-7 gap-0.5">
      {days.map((day) => (
        <DayCell
          key={day}
          day={day}
          visibleMonth={visibleMonth}
          mode={mode}
          selectedDate={selectedDate}
          referenceDate={referenceDate}
          datesWithSnapshot={datesWithSnapshot}
          onPickDay={onPickDay}
          t={t}
          languageCode={languageCode}
        />
      ))}
    </div>
  );
}

type DayCellProps = {
  day: string;
  visibleMonth: string;
  mode: PeriodMode;
  selectedDate: string;
  referenceDate: string;
  datesWithSnapshot: Set<string>;
  onPickDay: (day: string) => void;
  t: ReturnType<typeof getTranslations>;
  languageCode: ReturnType<typeof useScreen0Language>["languageCode"];
};

function DayCell({
  day,
  visibleMonth,
  mode,
  selectedDate,
  referenceDate,
  datesWithSnapshot,
  onPickDay,
  t,
  languageCode,
}: DayCellProps) {
  const inVisibleMonth = isSameMonth(day, visibleMonth);
  const isToday = day === referenceDate;
  const isSelected = day === selectedDate;
  const inActiveWeek = mode === "week" && isDateInWeek(day, selectedDate);
  const inActiveMonth = mode === "month" && isDateInMonth(day, selectedDate);
  const isFuture = isDateKeyAfter(day, referenceDate);
  const hasSnapshot = datesWithSnapshot.has(day);
  const dayNumber = parseDateKey(day).getDate();

  let cellClass =
    "relative grid min-h-10 place-items-center rounded-lg text-sm font-medium transition-colors ";
  if (!inVisibleMonth) {
    cellClass += "text-[oklch(46%_0.02_155)] ";
  } else if (isFuture) {
    cellClass += "cursor-not-allowed text-[oklch(42%_0.02_155)] ";
  } else {
    cellClass += "text-[oklch(86%_0.02_150)] active:bg-[oklch(24%_0.03_165)] ";
  }
  if (inActiveWeek && inVisibleMonth && !isSelected) {
    cellClass += "bg-[oklch(22%_0.035_160)] ";
  }
  if (inActiveMonth && inVisibleMonth && !isSelected && mode === "month") {
    cellClass += "ring-1 ring-inset ring-[oklch(34%_0.04_155)] ";
  }
  if (isSelected) {
    cellClass += "bg-[oklch(42%_0.08_155)] font-semibold text-[oklch(96%_0.02_150)] ";
  }

  return (
    <button
      type="button"
      className={cellClass}
      disabled={isFuture}
      aria-label={formatDayAriaLabel(day, isToday, isSelected, t, languageCode)}
      aria-pressed={isSelected}
      onClick={() => onPickDay(day)}
    >
      {isToday ? (
        <span
          className="absolute top-1.5 left-1/2 h-[2px] w-3 -translate-x-1/2 rounded-full bg-[oklch(78%_0.02_150/0.55)]"
          aria-hidden
        />
      ) : null}
      {dayNumber}
      {hasSnapshot && !isFuture ? (
        <span
          className={`absolute bottom-[5px] left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[oklch(72%_0.09_155)] ${inVisibleMonth ? "" : "opacity-40"}`}
          aria-hidden
        />
      ) : null}
    </button>
  );
}

function formatDayAriaLabel(
  day: string,
  isToday: boolean,
  isSelected: boolean,
  t: ReturnType<typeof getTranslations>,
  languageCode: ReturnType<typeof useScreen0Language>["languageCode"],
) {
  const locale = getScreen0IntlLocale(languageCode);
  const label = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(parseDateKey(day));
  if (isSelected && isToday) return formatTemplate(t.today.dayAriaSelectedToday, { label });
  if (isSelected) return formatTemplate(t.today.dayAriaSelected, { label });
  if (isToday) return formatTemplate(t.today.dayAriaToday, { label });
  return label;
}
